import {
  AssignmentCancelTypeEnum,
  AssignmentDeviationEnum,
  AssignmentModel,
  AssignmentStatusEnum,
  AssignmentUpdateAssignmentEventType,
  AssignmentUpdateAssignmentStatusEventType,
  AssignmentUpdateAssignmentStatusNewStatus,
  AssignmentViewModel,
  AssignmentViewModelBody,
  CaseEventType,
  CaseExtendedModel,
  StopoverAddress,
  StopOverRequest,
  ZipCodeArea,
} from 'api/model';
import Modal from 'components/Modal';
import useModalStack from 'contexts/modal/useModalStack';
import { useCallback, useMemo } from 'react';
import CancelCaseAssignmentModal from '../CancelCaseAssignmentModal';
import { RequestStatus, useApiCallFn } from 'hooks/useApiCallFn';
import { validateMoveToApproved } from 'utils/assignmentStatusValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import StopOverModal from '../StopOverModal';
import validateExpectedExpenses from 'utils/validateExpectedExpenses';
import MissingExpenseApprovalModal from '../MissingExpenseApprovalModal';
import {
  useAssignmentCancelAssignment,
  useAssignmentSetAssignmentToPlannedStopOver,
  useAssignmentSetAssignmentToStopOverHook,
  useAssignmentUpdateAssignment,
  useAssignmentUpdateAssignmentStatus,
} from 'api/assignment/assignment';
import { useCaseCloseCase } from 'api/case/case';
import PlannedStopOverModal from '../PlannedStopOverModal';
import OnHoldReasonModal from 'components/AssignmentsList/OnHoldReasonModal';

function getEventTypeAndMessageForAssignmentStatus(
  assignmenStatus: AssignmentStatusEnum
): [eventType: CaseEventType, message: string] {
  switch (assignmenStatus) {
    case AssignmentStatusEnum.Approved:
      return [CaseEventType.AssignmentApproved, 'Uppdrag godkänt'];
    case AssignmentStatusEnum.Canceled:
      return [CaseEventType.AssignmentDeleted, 'Uppdrag avbokat'];
    case AssignmentStatusEnum.Aborted:
      return [CaseEventType.AssignmentDeleted, 'Uppdrag bomkörning'];
    case AssignmentStatusEnum.Discarded:
      return [CaseEventType.AssignmentDeleted, 'Uppdrag borttaget'];
    default:
      return [CaseEventType.AssignmentEdited, 'Uppdrag redigerades'];
  }
}

const useCaseAssignmentActions = (
  currentCase: CaseExtendedModel | undefined,
  currentCaseWasUpdated: () => Promise<any>
) => {
  const modalStack = useModalStack();

  const updateCaseAssignmentCall = useAssignmentUpdateAssignment();

  const cancelAssignmentCall = useAssignmentCancelAssignment();

  const closeCaseCall = useCaseCloseCase();

  const setAssignmentToStopOver = useAssignmentSetAssignmentToStopOverHook();
  const stopoverAssignmentCall = useApiCallFn(
    (stopoverRequest: StopOverRequest) =>
      setAssignmentToStopOver(stopoverRequest)
  );

  const plannedStopoverAssignmentCall =
    useAssignmentSetAssignmentToPlannedStopOver();

  const updateAssignmentStatusCall = useAssignmentUpdateAssignmentStatus();

  const makeAssignmentViewModel = useCallback(
    (assignmentId: number): AssignmentViewModel | undefined => {
      const caseModel = currentCase?.case;
      const assignmentModel = currentCase?.case.assignmentList.find(
        (a) => a.assignmentID === assignmentId
      );
      if (!caseModel || !assignmentModel) return undefined;

      return {
        case: caseModel,
        assignment: assignmentModel,
      } satisfies AssignmentViewModel;
    },
    [currentCase?.case]
  );

  const handleCancelCaseAssignment = useCallback(
    async (assignmentId: number) => {
      const avm = makeAssignmentViewModel(assignmentId);
      if (!avm || !currentCase) return;

      const modalId = modalStack.push(
        <CancelCaseAssignmentModal
          assignmentViewModel={avm}
          linkedCases={currentCase?.linkedCases}
          onCloseClick={() => modalStack.pop(modalId)}
          onOkClick={async (
            assignmentStatus,
            assignmendDeviation,
            cancelType,
            closeCase,
            clearEstimatedAndFixedAssignmentTimes,
            otherAssignmentIdsToCancelWithinTheCase,
            endTime,
            comment,
            standbyEligible,
            linkedTradeInCaseToCancel,
            keepStayOver
          ) => {
            // // cancel this and any other assignments within the case
            // const updatedAVM = new AssignmentViewModel({
            //   ...avm,
            //   assignment: new AssignmentModel({
            //     ...avm.assignment,
            //     assignmentDeviationID: assignmendDeviation,
            //     assignmentStatusID: assignmentStatus,
            //     assignmentCancelTypeID: cancelType,
            //     endTime: endTime ?? avm.assignment.endTime,
            //     fixedAssignmentTime: clearEstimatedAndFixedAssignmentTimes
            //       ? undefined
            //       : avm.assignment.fixedAssignmentTime,
            //     // we need to free up the time after this assignment for booking if it hasn't already ended
            //     estimatedDuration:
            //       clearEstimatedAndFixedAssignmentTimes &&
            //       !avm.assignment.endTime
            //         ? getTimeFromNowInMillis(avm.assignment.startTime)
            //         : avm.assignment.estimatedDuration,
            //   }),
            //   case: new CaseModel({
            //     ...avm.case,
            //     caseStatusID: closeCase
            //       ? CaseStatusEnum.Closed
            //       : avm.case.caseStatusID,
            //   }),
            // });

            // const [eventType, defaultMessage] =
            //   getEventTypeAndMessageForAssignmentStatus(assignmentStatus);

            await cancelAssignmentCall.mutateAsync({
              data: {
                caseId: avm.case.caseID,
                assignmentStatus,
                assignmendDeviation,
                cancelType,
                closeCase,
                clearEstimatedAndFixedAssignmentTimes,
                assignmentIdsToCancelWithinTheCase: [
                  avm.assignment.assignmentID,
                  ...otherAssignmentIdsToCancelWithinTheCase,
                ],
                endTime,
                comment,
                standbyEligible,
                keepStayOver,
              },
            });

            // Om kopplat inbyte ska avbokas, gör ett separat anrop
            if (linkedTradeInCaseToCancel) {
              // "Avbok ej anlänt"/"Bom anlänt" får inte sättas på ett inbyte vars
              // hemleverans är avbokad (backend blockerar). Inbytet blev aldrig av
              // som eget uppdrag — det avbokas som "Avbok ej påbörjat (samma dag)".
              const remapArrivedCancelType = (
                [
                  AssignmentCancelTypeEnum.CanceledNotArrived,
                  AssignmentCancelTypeEnum.CanceledArrived,
                ] as AssignmentCancelTypeEnum[]
              ).includes(cancelType);

              await cancelAssignmentCall.mutateAsync({
                data: {
                  caseId: linkedTradeInCaseToCancel.caseId,
                  assignmentStatus: remapArrivedCancelType
                    ? AssignmentStatusEnum.Canceled
                    : assignmentStatus,
                  assignmendDeviation: remapArrivedCancelType
                    ? AssignmentDeviationEnum.Canceled
                    : assignmendDeviation,
                  cancelType: remapArrivedCancelType
                    ? AssignmentCancelTypeEnum.CanceledNotStartedSameDay
                    : cancelType,
                  closeCase: false,
                  clearEstimatedAndFixedAssignmentTimes: true,
                  assignmentIdsToCancelWithinTheCase: [
                    linkedTradeInCaseToCancel.assignmentId,
                  ],
                  endTime: remapArrivedCancelType
                    ? (endTime ?? new Date())
                    : endTime,
                  comment,
                  standbyEligible: false, // Inbyten får aldrig standby-tid
                },
              });
            }

            // await updateCaseAssignmentCall.mutateAsync({
            //   data: {
            //     ...updatedAVM,
            //   } as AssignmentViewModelBody,
            //   params: {
            //     eventType,
            //     eventMessage: comment || defaultMessage,
            //   },
            // });

            // inform the app about the update
            await currentCaseWasUpdated();
            modalStack.pop(modalId);
          }}
        />
      );
    },
    [
      makeAssignmentViewModel,
      currentCase,
      modalStack,
      cancelAssignmentCall,
      currentCaseWasUpdated,
    ]
  );

  const handleCaseAssignmentStopOver = useCallback(
    async (assignmentId: number) => {
      const avm = makeAssignmentViewModel(assignmentId);
      if (!avm || !currentCase) return;

      const modalId = modalStack.push(
        <StopOverModal
          onAddressPicked={async (name, address, zip, city, county) => {
            modalStack.pop(modalId);

            const stopOverRequest: StopOverRequest = {
                model: avm,
              stopOverAddress: {
                  name,
                  address,
                  zip,
                  city,
                  county: (county as ZipCodeArea) ?? undefined,
              } satisfies StopoverAddress,
            };

            await stopoverAssignmentCall.run(stopOverRequest);

            await currentCaseWasUpdated();
          }}
          onCancel={() => modalStack.pop(modalId)}
          originalAssignment={avm.assignment}
        />
      );
    },
    [
      currentCase,
      currentCaseWasUpdated,
      makeAssignmentViewModel,
      modalStack,
      stopoverAssignmentCall,
    ]
  );

  const handlePlannedCaseAssignmentStopOver = useCallback(
    async (assignmentId: number, preselectedCompanyId?: number) => {
      const avm = makeAssignmentViewModel(assignmentId);
      if (!avm || !currentCase) return;

      const modalId = modalStack.push(
        <PlannedStopOverModal
          currentCaseType={currentCase.case.caseTypeID}
          preselectedCompanyId={preselectedCompanyId}
          onAddressPicked={async (
            { name, address, zip, city, county },

            firstAssignmentBookedTo,
            firstAssignmentEstimatedDuration,
            firstAssignmentEstimatedStartTime,

            secondAssignmentBookedTo,
            secondAssignmentEstimatedDuration,
            secondAssignmentEstimatedStartTime
          ) => {
            modalStack.pop(modalId);

            await plannedStopoverAssignmentCall.mutateAsync({
              data: {
                model: avm,
                stopoverAddress: {
                  name,
                  address,
                  zip,
                  city,
                  county: (county as ZipCodeArea) ?? undefined,
                } satisfies StopoverAddress,

                firstAssignmentBookedTo,
                firstAssignmentEstimatedDuration,
                firstAssignmentEstimatedStartTime,

                secondAssignmentBookedTo,
                secondAssignmentEstimatedDuration,
                secondAssignmentEstimatedStartTime,
              },
            });

            await currentCaseWasUpdated();
          }}
          onCancel={() => modalStack.pop(modalId)}
          originalAssignment={avm.assignment}
        />
      );
    },
    [
      currentCase,
      currentCaseWasUpdated,
      makeAssignmentViewModel,
      modalStack,
      plannedStopoverAssignmentCall,
    ]
  );

  const handleDiscardCaseAssignment = useCallback(
    async (assignmentId: number) => {
      const avm = makeAssignmentViewModel(assignmentId);
      if (!avm || !currentCase) return;

      const modalId = modalStack.push(
        <Modal
          buttons={[
            {
              label: 'Ta bort',
              icon: <FontAwesomeIcon icon={faTrash} />,
              onClick: async () => {
                const updatedAVM: AssignmentViewModel = {
                  ...avm,
                  assignment: {
                    ...avm.assignment,
                    assignmentStatusID: AssignmentStatusEnum.Discarded,
                    assignmentDeviationID: AssignmentDeviationEnum.Discarded,
                  } satisfies AssignmentModel,
                };

                const [eventType, defaultMessage] =
                  getEventTypeAndMessageForAssignmentStatus(
                    AssignmentStatusEnum.Discarded
                  );

                try {
                  await updateCaseAssignmentCall.mutateAsync({
                    data: updatedAVM as AssignmentViewModelBody,
                    params: {
                      // Cast needed: NSwag generates CaseEventType as a TS enum, while Orval
                      // generates AssignmentUpdateAssignmentEventType as a const-object union.
                      // The numeric values are identical but TypeScript sees them as incompatible types.
                      eventType: eventType as unknown as AssignmentUpdateAssignmentEventType,
                      eventMessage: defaultMessage,
                    },
                  });
                } catch {
                  // Keep the confirm modal open on failure — a discard that fails (e.g.
                  // 409 concurrency) must not look like it succeeded. The error toast
                  // comes from the global MutationCache.onError handler in App.tsx.
                  return;
                }
                modalStack.pop(modalId);
                await currentCaseWasUpdated();
              },
            },
            { label: 'Avbryt', onClick: () => modalStack.pop(modalId) },
          ]}
          onClose={() => modalStack.pop(modalId)}
          title="Ta bort uppdrag?"
        >
          Är du säker på att du vill ta bort uppdraget?
        </Modal>
      );
    },
    [
      currentCase,
      currentCaseWasUpdated,
      makeAssignmentViewModel,
      modalStack,
      updateCaseAssignmentCall,
    ]
  );

  const approveCaseAssignment = useCallback(
    async (assignment: AssignmentViewModel) => {
      const updatedAVM: AssignmentViewModel = {
        ...assignment,
        assignment: {
          ...assignment.assignment,
          assignmentStatusID: AssignmentStatusEnum.Approved,
        } satisfies AssignmentModel,
      };

      const [eventType, defaultMessage] =
        getEventTypeAndMessageForAssignmentStatus(
          updatedAVM.assignment.assignmentStatusID
        );

      await updateCaseAssignmentCall.mutateAsync({
        data: {
          ...updatedAVM,
        } as AssignmentViewModelBody,
        params: {
          // Cast needed: NSwag generates CaseEventType as a TS enum, while Orval
          // generates AssignmentUpdateAssignmentEventType as a const-object union.
          // The numeric values are identical but TypeScript sees them as incompatible types.
          eventType: eventType as unknown as AssignmentUpdateAssignmentEventType,
          eventMessage: defaultMessage,
        },
      });
      await currentCaseWasUpdated();
      modalStack.pop();
    },
    [currentCaseWasUpdated, modalStack, updateCaseAssignmentCall]
  );

  const handleApproveCaseAssignment = useCallback(
    async (assignmentId: number) => {
      const viewModel = makeAssignmentViewModel(assignmentId);

      if (!viewModel) return;

      const errors = validateMoveToApproved(viewModel);

      if (errors.length > 0) {
        modalStack.push(
          <Modal
            buttons={[{ label: 'Ok', onClick: () => modalStack.pop() }]}
            onClose={() => modalStack.pop()}
            title="Fel"
          >
            Innan du kan godkänna uppdraget måste du:
            <br />
            <br />
            <ul>
              {errors.map((err) => (
                <li key={err}> - {err}</li>
              ))}
            </ul>
          </Modal>
        );
      } else {
        if (!currentCase) return;

        const missingExpenses = validateExpectedExpenses(
          currentCase,
          assignmentId
        );

        if (missingExpenses.missingExpenses.length > 0) {
          modalStack.push(
            <MissingExpenseApprovalModal
              handleClose={() => modalStack.pop()}
              handleConfirm={async () => {
                modalStack.pop();
                await approveCaseAssignment(viewModel);
              }}
              missingExpenses={[missingExpenses]}
              numSelected={1}
            />
          );
        } else {
          modalStack.pop();
          await approveCaseAssignment(viewModel);
        }
      }
    },
    [makeAssignmentViewModel, modalStack, currentCase, approveCaseAssignment]
  );

  const handleOnHoldCaseAssignment = useCallback(
    (assignmentId: number) => {
      const viewModel = makeAssignmentViewModel(assignmentId);
      if (!viewModel) return;

      modalStack.push(
        <OnHoldReasonModal
          onConfirm={async (reason) => {
            modalStack.pop();
            await updateAssignmentStatusCall.mutateAsync({
              data: viewModel as AssignmentViewModelBody,
              params: {
                newStatus: AssignmentStatusEnum.OnHold as unknown as AssignmentUpdateAssignmentStatusNewStatus,
                eventType: CaseEventType.AssignmentOnHold as unknown as AssignmentUpdateAssignmentStatusEventType,
                eventMessage: reason,
              },
            });
            await currentCaseWasUpdated();
          }}
          onClose={() => modalStack.pop()}
        />
      );
    },
    [makeAssignmentViewModel, modalStack, updateAssignmentStatusCall, currentCaseWasUpdated]
  );

  return useMemo(
    () => ({
      onCaseAssignmentCancel: handleCancelCaseAssignment,
      onCaseAssignmentStopOver: handleCaseAssignmentStopOver,
      onPlannedCaseAssignmentStopOver: handlePlannedCaseAssignmentStopOver,
      onCaseAssignmentDiscard: handleDiscardCaseAssignment,
      onCaseAssignmentApprove: handleApproveCaseAssignment,
      onCaseAssignmentOnHold: handleOnHoldCaseAssignment,
      isUpdatingAssignmentStatus:
        stopoverAssignmentCall.status === RequestStatus.Fetching ||
        closeCaseCall.status === 'loading',
    }),
    [
      closeCaseCall.status,
      handleApproveCaseAssignment,
      handleCancelCaseAssignment,
      handleCaseAssignmentStopOver,
      handleDiscardCaseAssignment,
      handleOnHoldCaseAssignment,
      handlePlannedCaseAssignmentStopOver,
      stopoverAssignmentCall.status,
    ]
  );
};

export default useCaseAssignmentActions;
